import {Component, OnDestroy, QueryList, TemplateRef, ViewChild, ViewChildren, ChangeDetectionStrategy} from '@angular/core';
import {saveAs} from "file-saver";
import {SortableDirective, SortEvent} from "@app/_directives/sortable.directive";
import {
    BackoutRequest,
    BackoutResponse,
    Customer,
    EdiFileSummaryFilter,
    EdiFileSummaryResponse,
    EdiInvalidIdentifier,
    ReprocessingFile,
    ReprocessingResponse
} from "@app/_models/api";
import {BehaviorSubject, Observable, Subscription} from "rxjs";
import {GlobalService} from "@app/_services/global.service";
import {ActivatedRoute, RouterLink} from "@angular/router";
import {EdiFileBaseService} from "@app/processing/services/edi-file-base.service";
import {AsyncPipe, formatDate, NgClass} from "@angular/common";
import {
    NgbDropdown,
    NgbDropdownAnchor,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbModal,
    NgbModalRef,
    NgbPagination
} from "@ng-bootstrap/ng-bootstrap";
import {FormsModule} from "@angular/forms";
import {ToastService} from "@app/_services/toast.service";
import {ClipboardService} from "@app/_services/clipboard.service";
import {ModalConfirmComponent} from "@app/_components/modal-confirm/modal-confirm.component";
import {EdiFileSummaryFilterComponent} from "@app/processing/edi-file-summary-filter/edi-file-summary-filter.component";
import {EdiFileInvalidIdentifiersService} from "@app/processing/services/edi-file-invalid-identifiers.service";
import {TablePagerComponent} from "@app/_components/table-pager/table-pager.component";

@Component({
    selector: 'app-edi-file-summary-base',
    imports: [
        TablePagerComponent,
        NgbPagination,
        AsyncPipe,
        FormsModule,
        NgClass,
        RouterLink,
        SortableDirective,
        NgbDropdown,
        NgbDropdownAnchor,
        NgbDropdownMenu,
        NgbDropdownItem,
        ModalConfirmComponent,
        EdiFileSummaryFilterComponent
    ],
    templateUrl: './edi-file-summary-base.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./edi-file-summary-base.component.scss', '../../app.component.scss']
})
export abstract class EdiFileSummaryBaseComponent implements OnDestroy {
    @ViewChildren(SortableDirective) headers!: QueryList<SortableDirective>;
    @ViewChildren(SortableDirective) invalidIdentifierHeaders!: QueryList<SortableDirective>;
    @ViewChild('confirmationModal') modalConfirmComponent!: ModalConfirmComponent;
    @ViewChild(EdiFileSummaryFilterComponent) filterComponent!: EdiFileSummaryFilterComponent;
    @ViewChild('invalidIdentifiersModal') invalidIdentifiersModal!: TemplateRef<any>;
    fileType: string = '';
    total$: Observable<number> | undefined;
    customers: Customer[] = [];
    customerId: string | undefined;
    tradingPartnerNames: string[] = [];
    tradingPartnerName: string | undefined;
    selectedFiles: Map<string, EdiFileSummaryResponse> = new Map();
    isCustomFilterApplied: boolean = false;
    currentFilter: EdiFileSummaryFilter | null = null;
    invalidIdentifiersModalRef!: NgbModalRef;
    protected readonly formatDate = formatDate;
    private _customers$ = new BehaviorSubject<Customer[]>([]);
    private customersSubscription: Subscription;

    protected constructor(
        public globalService: GlobalService,
        private route: ActivatedRoute,
        public ediFileService: EdiFileBaseService,
        public invalidIdentifiersService: EdiFileInvalidIdentifiersService,
        private toastService: ToastService,
        private modalService: NgbModal,
        private clipboardService: ClipboardService
    ) {
        this.fileType = ediFileService.fileType;

        const resolved = this.route.snapshot.data['resolvedData'];
        this._customers$.next(resolved.customers);
        this.tradingPartnerNames = resolved.tradingPartnerNames;
        this.customersSubscription = this._customers$.subscribe(customers => {
            this.customers = customers;
        });
        this.total$ = this.ediFileService.total$;
    }

    ngOnDestroy() {
        this.customersSubscription.unsubscribe();
    }

    get hasFilter(): boolean {
        return !!this.customerId || !!this.tradingPartnerName;
    }

    onCustomerChange(event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.customerId = value || undefined;
        this.loadFiles();
    }

    onTradingPartnerChange(event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.tradingPartnerName = value || undefined;
        this.loadFiles();
    }

    private loadFiles() {
        this.selectedFiles.clear();

        if (!this.hasFilter) {
            this.ediFileService.data = [];
            this.ediFileService.refreshDataSource();
            return;
        }

        const filter: EdiFileSummaryFilter = {
            ...this.currentFilter,
            customerId: this.customerId ?? null,
            tradingPartnerName: this.tradingPartnerName ?? null,
        };
        this.currentFilter = filter;
        this.globalService.showWaitOverlay();
        this.ediFileService.getFiles(filter).subscribe({
            next: (response) => this.onNextEDiFileSummaryResponse(response),
            error: (err) => {
                this.globalService.hideWaitOverlay();
                this.toastService.showDanger("Unknown Error", "Unable to load file summary.");
                console.error(err);
            }
        });
    }

    onOpenFilter() {
        if (!!this.filterComponent) {
            this.filterComponent.onOpen();
        }
    }

    onFilter(filter: EdiFileSummaryFilter) {
        this.globalService.showWaitOverlay();
        this.currentFilter = filter;
        this.ediFileService.getFiles(filter).subscribe({
            next: (response) => this.onNextEDiFileSummaryResponse(response),
            error: () => this.globalService.hideWaitOverlay()
        });
    }

    protected onNextEDiFileSummaryResponse (response: EdiFileSummaryResponse[]){
        this.ediFileService.data = response;
        this.ediFileService.refreshDataSource();
        this.globalService.hideWaitOverlay();
    }

    onCustomFilterApplied(isApplied: boolean) {
        this.isCustomFilterApplied = isApplied;
    }

    onSort({column, direction}: SortEvent) {
        for (const header of this.headers) {
            if (header.sortable !== column) {
                header.direction = '';
            }
        }

        this.ediFileService.sortColumn = column;
        this.ediFileService.sortDirection = direction;
    }

    onSortInvalidIdentifier({column, direction}: SortEvent) {
        for (const header of this.invalidIdentifierHeaders) {
            if (header.sortable !== column) {
                header.direction = '';
            }
        }

        this.invalidIdentifiersService.sortColumn = column;
        this.invalidIdentifiersService.sortDirection = direction;
    }

    onDownloadFile(fileGuid: string): void {
        this.ediFileService.downloadRawFile(fileGuid);
    }

    onDownloadCleansedFile(fileGuid: string): void {
        this.ediFileService.downloadCleansedFile(fileGuid);
    }

    copyFileName(fileName: string): Promise<void> {
        return this.clipboardService.copyToClipboard(fileName, "File name");
    }

    exportCsv(): void {
        const rows = this.ediFileService.data.filter(element => this.ediFileService.matches(element, this.ediFileService.searchTerm));

        if (rows.length === 0) {
            this.toastService.showInfo("Export CSV", "There are no files matching the current filter to export.");
            return;
        }

        const header = ["Customer", "Partner", "File", "Lines", "Load Time", "Reporting Start", "Reporting End", "Invalid Distribution Centers", "Invalid Products"];
        const csvRows = rows.map(element => [
            element.customerName,
            element.tradingPartnerName,
            element.fileName,
            element.lineCount,
            element.loadTime ? this.formatDate(element.loadTime + "Z", "yyyy-MM-dd HH:mm", "en-US") : "",
            element.minReportingDate ? this.formatDate(element.minReportingDate, "yyyy-MM-dd", "en-US") : "",
            element.maxReportingDate ? this.formatDate(element.maxReportingDate, "yyyy-MM-dd", "en-US") : "",
            element.hasInvalidDistributionCenters ? "Yes" : "No",
            element.hasInvalidProducts ? "Yes" : "No"
        ]);
        // CSV rows follow this.ediFileService.data's current order, which is whatever the
        // on-screen grid was last sorted to (TableOptionsService.sort mutates data in place).
        const csv = [header, ...csvRows].map(row => row.map(EdiFileSummaryBaseComponent.toCsvField).join(",")).join("\r\n");

        const timestamp = this.formatDate(new Date(), "yyyyMMdd-HHmm", "en-US");
        // Leading BOM so Excel on Windows opens the file as UTF-8 instead of the system codepage.
        const csvWithBom = "﻿" + csv;
        saveAs(new Blob([csvWithBom], {type: "text/csv;charset=utf-8;"}), `edi-${this.fileType}-file-summary-${timestamp}.csv`);
    }

    public static toCsvField(value: string | number): string {
        let text = String(value ?? "");
        // Prevent CSV/formula injection: a leading =, +, -, @, tab, or CR is interpreted as
        // a formula (or hides one) in Excel/Sheets. Prefix with a tab so it renders as-is.
        if (/^[=+\-@\t\r]/.test(text)) {
            text = `\t${text}`;
        }
        return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    }

    onReprocessingRequest(): void {
        if (this.selectedFiles.size === 0) {
            this.toastService.showInfo("Reprocessing Request", "Please select at least one file to reprocess.");
            return;
        }

        const selectedFileElements = Array.from(this.selectedFiles.values());
        const files: ReprocessingFile[] = selectedFileElements.map(({fileGuid, customerId}) => ({fileGuid, customerId}));
        const previouslyReprocessedFiles = selectedFileElements.filter(file => file.lastReprocessingRequestTime);

        if (previouslyReprocessedFiles.length === 0) {
            this.reprocessFiles(files);
            return;
        }

        let confirmationMessage = `<p>The following files have been previously reprocessed:</p><ul>`;
        for (const file of previouslyReprocessedFiles) {
            confirmationMessage += `
                <li>
                    <div class="text-ellipsis fw-bold" title="${file.fileName}">${file.fileName}</div>
                    <dl class="ms-3">
                        <dt>Last Requested By</dt>
                        <dd>${file.lastReprocessingRequestUser}</dd>
                        <dt>Last Requested Time</dt>
                        <dd>${this.longFormatDate(file.lastReprocessingRequestTime)}</dd>
                    </dl>
                </li>`;
        }
        confirmationMessage += `</ul><p>Are you sure you want to reprocess these files?</p>`;

        this.modalConfirmComponent.open(confirmationMessage);

        this.modalConfirmComponent.modalRef.result.then(
            () => this.reprocessFiles(files),
            () => {
            }
        );
    }

    onBackoutRequest(): void {
        if (this.selectedFiles.size === 0) {
            this.toastService.showInfo("Back Out Request", "Please select at least one file to back out.");
            return;
        }

        const requests: BackoutRequest[] = Array.from(
            this.selectedFiles.values(),
            ({fileGuid, fileName, customerId}) => ({fileGuid, fileName, customerId})
        );

        const confirmationMessage = "Are you sure you want to back out these files?";

        this.modalConfirmComponent.open(confirmationMessage);

        this.modalConfirmComponent.modalRef.result.then(
            () => this.backoutFiles(requests),
            () => {
            }
        );
    }

    onShowInvalidIdentifiers(): void {
        this.globalService.showWaitOverlay("Loading invalid identifiers...");
        this.ediFileService.getInvalidIdentifiers(this.customerId!).subscribe({
            next: (items: EdiInvalidIdentifier[]) => {
                this.invalidIdentifiersService.data = items;
                this.invalidIdentifiersService.refreshDataSource();

                if (!items.length) {
                    this.toastService.showInfo("Invalid Identifiers", "No invalid identifiers were found.");
                    this.globalService.hideWaitOverlay();
                    return;
                }
                this.invalidIdentifiersModalRef = this.modalService.open(this.invalidIdentifiersModal, {
                    modalDialogClass: "modal-dialog-centered modal-lg",
                    backdrop: "static",
                });
                this.globalService.hideWaitOverlay();
            },
            error: () => {
                this.globalService.hideWaitOverlay();
                this.toastService.showDanger("Invalid Identifiers", "Unable to load invalid identifiers.");
            }
        });
    }

    onInvalidIdentifiersDismiss() {
        if (this.invalidIdentifiersModalRef) {
            this.invalidIdentifiersModalRef.close();
        }
    }

    longFormatDate(input: string): string {
        if (!input) {
            return '';
        }
        return this.formatDate(input + 'Z', 'yyyy-MM-dd, hh:mm a', 'en-US')
    }

    rangeFormatDate(startDate: string | null, endDate: string | null): string {
        let formattedStartDate = !!startDate ? this.formatDate(startDate, 'yyyy-MM-dd', 'en-US') : "";
        let formattedEndDate = !!endDate ? this.formatDate(endDate, 'yyyy-MM-dd', 'en-US') : "";
        let returnValue = formattedStartDate;

        if (formattedStartDate != formattedEndDate) {
            returnValue = `${returnValue} - ${formattedEndDate}`;
        }

        return returnValue;
    }

    toggleFileSelection(element: EdiFileSummaryResponse): void {
        if (this.selectedFiles.has(element.fileGuid)) {
            this.selectedFiles.delete(element.fileGuid);
        }
        else {
            this.selectedFiles.set(element.fileGuid, element);
        }
    }

    isFileSelected(fileGuid: string): boolean {
        return this.selectedFiles.has(fileGuid);
    }

    isAllOnPageSelected(pageElements: EdiFileSummaryResponse[]): boolean {
        return pageElements.length > 0 && pageElements.every(element => this.isFileSelected(element.fileGuid));
    }

    toggleSelectAllOnPage(pageElements: EdiFileSummaryResponse[]): void {
        if (this.isAllOnPageSelected(pageElements)) {
            for (const element of pageElements) {
                this.selectedFiles.delete(element.fileGuid);
            }
        } else {
            for (const element of pageElements) {
                this.selectedFiles.set(element.fileGuid, element);
            }
        }
    }

    private reprocessFiles(files: ReprocessingFile[]): void {
        this.globalService.showWaitOverlay("Queueing file for reprocessing...");
        this.ediFileService.submitReprocessingRequest(files).subscribe({
            next: (response: ReprocessingResponse) => {
                this.toastService.showSuccess("Reprocessing Request", response.message);
            },
            error: (error) => {
                this.toastService.showDanger("Reprocessing Request", (!!error?.error?.detail ? error.error.detail : "An unexpected error occurred."));
                this.globalService.hideWaitOverlay();
            },
            complete: () => {
                this.globalService.hideWaitOverlay();
            }
        })
    }

    private backoutFiles(files: BackoutRequest[]): void {
        this.globalService.showWaitOverlay("Queueing file for back out...");
        this.ediFileService.submitBackoutRequest(files).subscribe({
            next: (response: BackoutResponse) => {
                this.toastService.showSuccess("Back Out Request", response.message);
            },
            error: (error) => {
                this.toastService.showDanger("Back Out Request", (!!error?.error?.detail ? error.error.detail : "An unexpected error occurred."));
                this.globalService.hideWaitOverlay();
            },
            complete: () => {
                this.globalService.hideWaitOverlay();
            }
        })
    }
}
